import { CARD_HEIGHT, CARD_WIDTH, Game } from '@phaser/scenes/Game';
import { CardObject } from '@phaser/objects/Card.object';
import { defaultText } from '@phaser/helpers/Text';
import i18next from 'i18next';

export const SPAWN_DURATION = 300;

export class CardBehavior {
  public maskRect: Phaser.GameObjects.Rectangle;

  private _scene: Game = Game.current;
  private _isPlayingKill = false;
  private _isPlayingHit = false;

  constructor(private _card: CardObject) {
    this._setSprite();
    this._setHeader();
  }

  public spawnAnimation() {
    const serverPos = this._card.data.core.pos;
    const { x, y } = this._scene.grid[serverPos];

    this._card.tweenTargets.forEach((target) => {
      target.setPosition(x, y);
    });

    this._scene.add.tween({
      targets: this._card.tweenTargets,
      scale: { from: 0.4, to: 1 },
      alpha: { from: 0, to: 1 },
      ease: 'Back.out',
      duration: SPAWN_DURATION,
    });
  }

  public abilityAnimation(textValue: string) {
    const { x, y } = this._card.container;

    const maskRect = this._scene.add
      .rectangle(x, y, CARD_WIDTH, CARD_HEIGHT, 0x000000)
      .setVisible(false);
    const mask = maskRect.createGeometryMask();

    const image = this._getCardSprite(false).setMask(mask);
    this._card.container.add(image);

    const text = this._scene.add.text(0, 0, textValue, {
      ...defaultText,
      fontSize: '20px',
      color: 'gold',
    }).setOrigin(0.5);
    this._card.container.add(text);

    this._scene.add.tween({
      targets: [image, maskRect],
      scale: '+=0.2',
      alpha: 0,
      ease: 'Power2',
      duration: 750,
      onComplete: () => {
        image.destroy();
        maskRect.destroy();
      },
    });

    this._scene.add.tween({
      targets: text,
      scale: { from: 1, to: 1.1 },
      ease: 'Power2',
      duration: 750,
      onComplete: () => {
        this._scene.add.tween({
          targets: text,
          alpha: 0,
          duration: 500,
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  public killAnimation() {
    if (this._isPlayingKill) return;
    this._isPlayingKill = true;

    // Phase 1: brief red-orange explosion flash
    const flashOverlay = this._scene.add.graphics().setDepth(11);
    this._card.container.add(flashOverlay);

    this._scene.tweens.addCounter({
      from: 0.95,
      to: 0,
      duration: 250,
      ease: 'Power3',
      onUpdate: (t) => {
        const v = t.getValue()!;
        flashOverlay.clear();
        flashOverlay.fillStyle(0xFF4400, v);
        flashOverlay.fillRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT);
      },
      onComplete: () => {
        flashOverlay.destroy();

        // Phase 2: card spins off-screen
        const { x, y } = this._getOffscreenPosition();
        const distance = Phaser.Math.Distance.Between(
          this._card.container.x, this._card.container.y, x!, y!,
        );

        this._scene.add.tween({
          rotation: Math.random() >= 0.5 ? Math.PI * 2.5 : -Math.PI * 2.5,
          onComplete: () => this._card.destroy(),
          targets: this._card.tweenTargets,
          duration: Math.max(300, distance * 1.1),
          ease: 'Power2',
          scale: 0,
          x,
          y,
        });
      },
    });
  }

  public damageAnimation() {
    if (this._isPlayingHit || this._isPlayingKill) return;

    // Scale punch
    this._scene.add.tween({
      onComplete: () => {
        this._isPlayingHit = false;
      },
      onStart: () => {
        this._isPlayingHit = true;
      },
      targets: this._card.tweenTargets,
      ease: 'Power2',
      duration: 55,
      scale: 0.87,
      yoyo: true,
    });

    // White flash overlay — makes hits feel real
    const flash = this._scene.add.rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, 0xFFFFFF, 0.75)
      .setDepth(11);
    this._card.container.add(flash);

    this._scene.add.tween({
      targets: flash,
      alpha: 0,
      duration: 140,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });
  }

  private _getOffscreenPosition() {
    const camera = this._scene.cameras.main;
    const viewRight = camera.displayWidth;
    const viewBottom = camera.displayHeight;

    const top = { x: Math.random() * camera.displayWidth, y: -CARD_HEIGHT / 2 };
    const right = { y: Math.random() * camera.displayHeight, x: viewRight + CARD_WIDTH / 2 };
    const bottom = { x: Math.random() * camera.displayWidth, y: viewBottom + CARD_HEIGHT / 2 };
    const left = { y: Math.random() * camera.displayHeight, x: -CARD_WIDTH / 2 };

    const compareArray = Math.random() > 0.5 ? [left, right] : [top, bottom];
    const { x, y } = compareArray.reduce((prev, curr) => {
      const d = Phaser.Math.Distance.Between(
        this._card.container.x, this._card.container.y, curr.x, curr.y,
      );
      const pd = Phaser.Math.Distance.Between(
        this._card.container.x, this._card.container.y, prev.x, prev.y,
      );
      return d > pd ? curr : prev;
    });

    return { x, y };
  }

  private _setSprite() {
    const camera = this._scene.cameras.main;

    this.maskRect = this._scene.add
      .rectangle(camera.width / 2, 0, CARD_WIDTH, CARD_HEIGHT, 0x000000)
      .setVisible(false);

    const image = this._getCardSprite();
    this._card.container.add(image);
  }

  private _setHeader() {
    const cardHeader = this._scene.add
      .rectangle(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, 18, 0xFFFFFF)
      .setOrigin(0);
    const name = i18next.t(`Factions.${this._card.data.enemy?.faction ?? 'Loot'}.${this._card.data.core.key}`);
    const enemyName = this._scene.add.text(CARD_WIDTH / 2 - 3, -CARD_HEIGHT / 2 + 9, name, {
      ...defaultText,
      strokeThickness: 0,
      color: 'black',
      fontSize: '10px',
    }).setOrigin(1, 0.5);

    this._card.container.add([cardHeader, enemyName]);
  }

  private _getCardSprite(useMask = true) {
    const faction = this._card.data.enemy?.faction ?? 'loot';
    const key = this._card.data.core.key;
    const name = `${faction.toLowerCase()}_${key}`;

    const scale = Math.max(CARD_HEIGHT / 512, CARD_WIDTH / 512);
    const image = this._scene.add.image(0, 18, 'sprites', name).setScale(scale);

    if (!useMask) return image;

    const mask = this.maskRect.createGeometryMask();
    return image.setMask(mask);
  }
}
